"use client";

import { useEffect, useMemo } from "react";
import { useSnapshot } from "valtio/react";
import log from "logbeacon";
import { rootStore } from "@/src/store";
import { getOrCreateClientUuid, getTracker } from "@/src/utils/openreplay";
import { useMemberUser } from "@/src/hooks/useMemberUser";

function retryUntilTrackerReady(fn: () => void) {
  let attempts = 0;
  const maxAttempts = 20;
  const tick = () => {
    attempts += 1;
    const tracker = getTracker();
    if (tracker) {
      fn();
      return;
    }
    if (attempts < maxAttempts) {
      setTimeout(tick, 250);
    }
  };
  tick();
}

export function useOpenReplayIdentity() {
  const { isLoggedIn, isLoading, isFetching } = useSnapshot(rootStore.login);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const { data: member } = useMemberUser();
  // 计算会员等级
  const plan = useMemo(() => {
    if (member?.isExpert) {
      return "export" as const;
    }
    if (member?.isPro) {
      return "pro" as const;
    }
    return "free" as const;
  }, [member?.isExpert, member?.isPro]);

  useEffect(() => {
    // Keep session continuity even before login.
    if (typeof window === "undefined") {
      return;
    }

    if (isLoading || isFetching) {
      return;
    }

    retryUntilTrackerReady(() => {
      const tracker = getTracker();
      if (!tracker) {
        return;
      }

      const clientUuid = getOrCreateClientUuid();

      if (isLoggedIn && ownerInfo?.id) {
        log.debug("[文案]ownerInfo.id", ownerInfo.id);
        tracker.setUserID?.(String(ownerInfo.id));
      } else {
        tracker.setUserID?.(clientUuid);
      }
      if (plan) {
        tracker.setMetadata?.("plan", plan);
      }
    });
  }, [isLoggedIn, ownerInfo?.id, isLoading, isFetching, plan]);
}
